import { Typography } from "antd";
import formatarDuracao from "../../utils/formatarDuracao";
import {
  batidasPorMinuto,
  haQuantoTempo,
  hora,
  partesDoDisco,
  segundosDeDesvio,
} from "./textos";

const { Text } = Typography;

const AZUL = "#1677ff";
const AZUL_CLARO = "#91caff";
const CIANO = "#13c2c2";
const VERDE = "#52c41a";
const LARANJA = "#fa8c16";
const CINZA = "#d9d9d9";
const TRILHO = "#e8edf4";

const estiloGadget = (aviso) => ({
  background: aviso ? "#fffbe6" : "#fafafa",
  border: `1px solid ${aviso ? "#ffe58f" : "transparent"}`,
  borderRadius: 8,
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 0,
});

const estiloValor = { fontWeight: 600, fontSize: 20, lineHeight: "28px" };

export function Gadget({ rotulo, valor, unidade, rodape, aviso, children }) {
  return (
    <div style={estiloGadget(aviso)}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {rotulo}
      </Text>
      {valor !== undefined && (
        <div style={estiloValor}>
          {valor}
          {unidade && (
            <Text
              type="secondary"
              style={{ fontSize: 13, fontWeight: 400, marginLeft: 4 }}>
              {unidade}
            </Text>
          )}
        </div>
      )}
      {children}
      {rodape && (
        <Text type="secondary" style={{ fontSize: 12, marginTop: "auto" }}>
          {rodape}
        </Text>
      )}
    </div>
  );
}

export function FaixaDeBarras({ faixas, altura = 22, cor = VERDE, dica }) {
  const largura = faixas.length * 10;
  return (
    <svg
      width="100%"
      height={altura}
      viewBox={`0 0 ${largura} ${altura}`}
      preserveAspectRatio="none">
      {faixas.map((f, i) => (
        <rect
          key={i}
          x={i * 10}
          y={f.recebido ? 0 : altura * 0.6}
          width={8}
          height={f.recebido ? altura : altura * 0.4}
          rx={1.5}
          fill={f.recebido ? cor : CINZA}>
          <title>{dica(f)}</title>
        </rect>
      ))}
    </svg>
  );
}

export function GadgetHeartbeat({ historico, ultimoHeartbeatEm, agora }) {
  const faixas = batidasPorMinuto(historico, agora);
  const recebidos = faixas.filter((f) => f.recebido).length;
  return (
    <Gadget
      rotulo="Heartbeat"
      valor={haQuantoTempo(ultimoHeartbeatEm, agora)}
      rodape={`${recebidos} de 30 minutos com sinal`}>
      <FaixaDeBarras
        faixas={faixas}
        dica={(f) =>
          `${hora(f.em)} · ${f.recebido ? "heartbeat recebido" : "sem heartbeat"}`
        }
      />
    </Gadget>
  );
}

export function GadgetRelogio({ relogio }) {
  const desvio = segundosDeDesvio(relogio?.desvioMs);
  if (desvio === null) {
    return <Gadget rotulo="Relógio" valor="-" rodape="Ainda não medido" />;
  }
  const limite = (relogio?.limiteMs || 5000) / 1000;
  const extremo = Math.max(limite * 2, Math.abs(desvio));
  const posicao = (v) => `${((v + extremo) / (2 * extremo)) * 100}%`;
  const fora = Boolean(relogio?.fora);
  return (
    <Gadget
      rotulo="Relógio"
      valor={`${desvio > 0 ? "+" : ""}${desvio.toLocaleString("pt-BR")} s`}
      unidade={desvio >= 0 ? "adiantado" : "atrasado"}
      aviso={fora}
      rodape={
        fora
          ? `⚠ Fora do limite de ${limite} s`
          : `Dentro do limite de ${limite} s`
      }>
      <div style={{ position: "relative", height: 16, marginTop: 4 }}>
        <div
          style={{
            position: "absolute",
            top: 5,
            left: 0,
            right: 0,
            height: 6,
            borderRadius: 3,
            background: "#ffe7ba",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 5,
            left: posicao(-limite),
            width: `${(limite / extremo) * 100}%`,
            height: 6,
            background: "#d9f7be",
          }}
        />
        <div
          title={`Desvio medido: ${desvio.toLocaleString("pt-BR")} s`}
          style={{
            position: "absolute",
            top: 1,
            left: posicao(desvio),
            width: 14,
            height: 14,
            marginLeft: -7,
            borderRadius: "50%",
            background: fora ? LARANJA : VERDE,
            border: "2px solid #fff",
          }}
        />
      </div>
    </Gadget>
  );
}

export function GadgetDisco({ disco }) {
  const dados = partesDoDisco(disco);
  if (!dados) {
    return <Gadget rotulo="Disco" valor="-" rodape="Sem medição do disco" />;
  }
  const cores = [AZUL, CIANO, TRILHO];
  const raio = 34;
  const circunferencia = 2 * Math.PI * raio;
  let acumulado = 0;
  const politica =
    disco.politica === "parar" ? "para a gravação" : "descarta os mais antigos";
  return (
    <Gadget
      rotulo="Disco"
      aviso={disco.cheio}
      rodape={`Buffer ${(disco.bufferGb ?? 0).toLocaleString("pt-BR")} de ${
        disco.bufferMaximoGb ?? "-"
      } GB · ${politica}`}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <svg
          width={84}
          height={84}
          viewBox="0 0 84 84"
          style={{ flex: "none" }}>
          {dados.partes.map((parte, i) => {
            const fracao = parte.valor / dados.total;
            const comprimento = Math.max(fracao * circunferencia - 2, 0.5);
            const deslocamento = -acumulado * circunferencia;
            acumulado += fracao;
            return (
              <circle
                key={parte.nome}
                cx={42}
                cy={42}
                r={raio}
                fill="none"
                stroke={cores[i]}
                strokeWidth={12}
                strokeDasharray={`${comprimento} ${circunferencia - comprimento}`}
                strokeDashoffset={deslocamento}
                transform="rotate(-90 42 42)">
                <title>{`${parte.nome}: ${parte.valor.toLocaleString(
                  "pt-BR"
                )} GB (${Math.round(fracao * 100)}%)`}</title>
              </circle>
            );
          })}
          <text
            x={42}
            y={40}
            textAnchor="middle"
            fontSize={13}
            fontWeight={600}
            fill="rgba(0,0,0,.88)">
            {`${dados.percentualLivre}%`}
          </text>
          <text
            x={42}
            y={53}
            textAnchor="middle"
            fontSize={10}
            fill="rgba(0,0,0,.45)">
            livre
          </text>
        </svg>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            fontSize: 12,
            flex: 1,
          }}>
          {dados.partes.map((parte, i) => (
            <span
              key={parte.nome}
              style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <i
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: cores[i],
                  flex: "none",
                }}
              />
              {parte.nome}
              <b style={{ marginLeft: "auto", paddingLeft: 8 }}>
                {`${parte.valor.toLocaleString("pt-BR")} GB`}
              </b>
            </span>
          ))}
        </div>
      </div>
    </Gadget>
  );
}

export function GadgetFila({ fila, historico, agora }) {
  const valores = (historico || []).map((h) => h.pendentes ?? 0);
  const maximo = Math.max(1, ...valores);
  const largura = 300;
  const altura = 26;
  const pontos = valores
    .map((v, i) => {
      const x = valores.length > 1 ? (i / (valores.length - 1)) * largura : 0;
      return `${x},${altura - (v / maximo) * (altura - 4)}`;
    })
    .join(" ");
  const maisAntigo = fila?.pendenteMaisAntigoEm;
  const minutos = maisAntigo
    ? Math.max(0, Math.round((agora - new Date(maisAntigo)) / 60000))
    : null;
  const espera =
    minutos === null
      ? "Nada esperando"
      : `Mais antigo há ${formatarDuracao(minutos)}`;
  return (
    <Gadget
      rotulo="Fila de envio"
      valor={fila?.pendentes ?? "-"}
      unidade="pendentes"
      aviso={minutos !== null && minutos > 120}
      rodape={`${espera} · ${fila?.falhos ?? 0} falhos`}>
      {valores.length > 1 && (
        <svg
          width="100%"
          height={altura}
          viewBox={`0 0 ${largura} ${altura}`}
          preserveAspectRatio="none">
          <polygon
            points={`0,${altura} ${pontos} ${largura},${altura}`}
            fill="#e6f4ff"
          />
          <polyline
            points={pontos}
            fill="none"
            stroke={AZUL}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </Gadget>
  );
}

export function GadgetUpload({ upload, historico }) {
  const amostras = (historico || []).slice(-30);
  const limite = upload?.kbpsLimite || 0;
  const maximo = Math.max(
    limite * 1.1,
    ...amostras.map((a) => a.uploadKbps || 0),
    1
  );
  const altura = 40;
  const largura = Math.max(amostras.length, 1) * 10;
  const y = (v) => altura - (v / maximo) * altura;
  return (
    <Gadget
      rotulo="Upload"
      valor={upload?.kbpsAtual?.toLocaleString("pt-BR") ?? "-"}
      unidade="kbps"
      rodape={`Últimos 30 min · ${
        limite ? `limite ${limite.toLocaleString("pt-BR")} kbps` : "sem limite"
      }`}>
      {amostras.length > 0 && (
        <svg
          width="100%"
          height={altura}
          viewBox={`0 0 ${largura} ${altura}`}
          preserveAspectRatio="none">
          {amostras.map((a, i) => {
            const valor = a.uploadKbps || 0;
            return (
              <rect
                key={a.em}
                x={i * 10}
                y={y(valor)}
                width={8}
                height={altura - y(valor)}
                rx={1.5}
                fill={i === amostras.length - 1 ? AZUL : AZUL_CLARO}>
                <title>{`${hora(a.em)} · ${valor.toLocaleString(
                  "pt-BR"
                )} kbps`}</title>
              </rect>
            );
          })}
          {limite > 0 && (
            <line
              x1={0}
              x2={largura}
              y1={y(limite)}
              y2={y(limite)}
              stroke="rgba(0,0,0,.45)"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
      )}
    </Gadget>
  );
}

export { AZUL, CINZA, LARANJA, TRILHO };
